// src/hooks/useSellLockRetry.js
//
// Reusable hook that handles the sell-lock retry pattern.
//
// BEHAVIOUR:
//   When the backend returns { sell_locked: true }, the call didn't fail —
//   the admin has temporarily locked sells.  We keep the spinner going and
//   silently retry every 5 s.  The moment the admin unlocks, the next retry
//   goes through and the sell completes automatically.
//
//   If the component unmounts (user closes modal / navigates away) while a
//   retry is pending, the retry is cancelled immediately — the sell will
//   NEVER fire in the background without the user on the page.
//
// USAGE (in any component that has a "convert to market" or "sell" button):
//
//   const { loading, attemptAction } = useSellLockRetry();
//
//   const handleConvertToMarket = (orderId) => {
//     attemptAction(
//       () => tradingService.convertToMarketOrder(orderId),   // the API call
//       (response) => { /* handle success — response is the resolved value */ },
//       (err)      => { /* handle real errors */ }
//     );
//   };
//
//   <button disabled={loading} onClick={() => handleConvertToMarket(order.id)}>
//     {loading ? 'Processing...' : 'Sell at Market Price'}
//   </button>

import { useState, useEffect, useRef, useCallback } from 'react';

const RETRY_INTERVAL_MS = 5000; // 5 seconds — matches backend poll interval

export function useSellLockRetry() {
  const [loading, setLoading]     = useState(false);
  const retryTimerRef             = useRef(null);
  const isMountedRef              = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  // apiFn     — async function that performs the request, returns the response object
  // onSuccess — called with the response when the action completes successfully
  // onError   — called with the Error when a real error occurs
  const attemptAction = useCallback((apiFn, onSuccess, onError) => {
    // Cancel any in-flight retry before starting a new attempt
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    setLoading(true);

    const run = async () => {
      try {
        const response = await apiFn();

        if (!isMountedRef.current) return;

        if (response && response.sell_locked) {
          // Still locked — schedule a silent retry, keep spinner going
          retryTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) run();
          }, RETRY_INTERVAL_MS);
          return;
        }

        // Success
        if (isMountedRef.current) {
          setLoading(false);
          onSuccess(response);
        }
      } catch (err) {
        if (!isMountedRef.current) return;
        setLoading(false);
        if (onError) onError(err);
      }
    };

    run();
  }, []);

  // cancelRetry allows the caller to manually stop a pending retry
  // (e.g. if the user cancels the limit order while it's spinning)
  const cancelRetry = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (isMountedRef.current) setLoading(false);
  }, []);

  return { loading, attemptAction, cancelRetry };
}